import React, { useState, useMemo, useEffect } from 'react';
import { BMP_MonthlyInvestment } from '../../../models/bmp.model';
import Decimal from 'decimal.js';

interface InvestmentSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (selectedItems: BMP_MonthlyInvestment[], prices: { btc: number; eth: number, liq: number }) => void;
    data: BMP_MonthlyInvestment[];
    isLoading?: boolean;
}

export const InvestmentSelectionModal: React.FC<InvestmentSelectionModalProps> = ({
    isOpen, 
    onClose, 
    onSubmit, 
    data,
    isLoading 
}) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [bitcoinPrice, setBitcoinPrice] = useState<string>('');
    const [ethereumPrice, setEthereumPrice] = useState<string>('');
    const [liquidityShare, setLiquidityShare] = useState<string>('');

    useEffect(() => {
        if (isOpen && data.length > 0) {
            setSelectedIds(data.map((item: any) => item.id));
        }
    }, [data, isOpen]);

    const currentSelectedTotal = useMemo(() => {
        return data
            .filter(item => selectedIds.includes(item.id))
            .reduce((acc, curr) => {
                const val = curr.investedUsd ? new Decimal(curr.investedUsd) : new Decimal(0);
                return acc.plus(val);
            }, new Decimal(0));
    }, [data, selectedIds]);

    const toggleSelection = (id: number) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleConfirm = () => {
        const selectedItems = data.filter(item => selectedIds.includes(item.id));
        const btc = parseFloat(bitcoinPrice);
        const eth = parseFloat(ethereumPrice);
        const liq = parseFloat(liquidityShare);

        if (isNaN(btc) || isNaN(eth) || btc <= 0 || eth <= 0) {
            alert("Kérlek adj meg érvényes BTC és ETH árfolyamot!");
            return;
        }

        if(isNaN(liq) || liq < 0) {
            alert("Kérlek adj meg érvényes likviditást!");
            return;
        }

        if (selectedItems.length === 0) {
            alert("Legalább egy befektetést ki kell választanod!");
            return;
        }

        onSubmit(selectedItems, { btc, eth, liq });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-amber-50">
                    <div>
                        <h2 className="text-2xl font-black text-amber-900">Befektetések kiválasztása</h2>
                        <p className="text-sm text-amber-700 opacity-70">Válaszd ki a most elszámolni kívánt tételeket</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl">&times;</button>
                </div>

                <div className="overflow-y-auto p-6 flex-grow">
                    <table className="w-full text-left">
                        <thead className="text-xs font-bold uppercase text-gray-400 border-b">
                            <tr>
                                <th className="pb-3">Név</th>
                                <th className="pb-3">Dátum</th>
                                <th className="pb-3 text-center">Hónap</th>
                                <th className="pb-3 text-right">Összeg</th>
                                <th className="pb-3 text-center">Kijelölés</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {data.map((item) => (
                                <tr key={item.id} className="hover:bg-amber-50/50">
                                    <td className="py-4 font-semibold text-gray-700">{item.user?.name || 'Ismeretlen'}</td>
                                    <td className="py-4 text-gray-500 text-sm">{new Date(item.date).toLocaleDateString()}</td>
                                    <td className="py-4 text-center text-gray-600 font-mono">{item.monthCounter}</td>
                                    <td className="py-4 text-right font-bold text-amber-700">
                                        {item.investedUsd ? new Decimal(item.investedUsd).toNumber().toLocaleString() : '0'} USD
                                    </td>
                                    <td className="py-4 text-center">
                                        <input 
                                            type="checkbox" 
                                            disabled={isLoading}
                                            checked={selectedIds.includes(item.id)}
                                            onChange={() => toggleSelection(item.id)}
                                            className="w-6 h-6 accent-amber-600 cursor-pointer"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="p-6 bg-gray-50 border-t flex flex-col gap-6">
                    <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">BTC Ár ($)</label>
                            <input type="number" step="any" value={bitcoinPrice} onChange={(e) => setBitcoinPrice(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none font-mono text-right" />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">ETH Ár ($)</label>
                            <input type="number" step="any" value={ethereumPrice} onChange={(e) => setEthereumPrice(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none font-mono text-right" />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Likviditás (%)</label>
                            <input type="number" step="any" value={liquidityShare} onChange={(e) => setLiquidityShare(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none font-mono text-right" />
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="text-gray-600">
                            Összesen: <span className="text-2xl font-bold text-amber-800 ml-2">{currentSelectedTotal.toNumber().toLocaleString()} USD</span>
                        </div>
                        <div className="flex gap-3 w-full md:w-auto">
                            <button onClick={onClose} disabled={isLoading} className="px-6 py-2 text-gray-500">Mégse</button>
                            <button onClick={handleConfirm} disabled={!bitcoinPrice || !ethereumPrice || isLoading} className="px-8 py-3 bg-amber-700 text-white rounded-xl font-bold shadow-md min-w-[180px]">
                                {isLoading ? "Feldolgozás..." : "Összeg befektetése"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};